import { useCallback, useRef } from 'react'

import { PREVIEW_MODE_STRATEGY } from '../registries/outlinePreviewRegistry.js'
import { HERO_OUTLINE }         from '../constants/heroOutline.js'
import { OUTLINE_PREVIEW_MODE } from '../constants/outlinePreviewMode.js'
import { SVG_OUTLINE_CLASS }    from '../constants/svgOutlineClasses.js'

// ── ID parsing ─────────────────────────────────────────────────────────────────

/**
 * Returns true when a raw SVG path `id` value looks like a building id_cad.
 * Building id_cad values are 1–2 alphanumeric characters (e.g. `0A`, `0B`, `01`).
 *
 * This is a format-only check. Pure integers like `"9"` or `"12"` also match
 * because multi-building projects can have building IDs `"10"`, `"11"`, `"12"`.
 * The hook factory's data filter (`buildingsData.some(b => b.id_cad === el.id)`)
 * ensures only real buildings get interaction wired.
 *
 * @param {string} id
 * @returns {boolean}
 */
export function isBuildingPathId(id) {
  if (typeof id !== 'string') return false
  return /^[0-9A-Za-z]{1,2}$/.test(id)
}

// ── Pure factory (testable without React) ────────────────────────────────────

/**
 * Creates a `wireListeners` function for building outline interaction.
 *
 * Separated from the React hook so it can be called and tested outside of a
 * React component context. The hook passes its `useRef` objects as `refs`.
 *
 * @param {object}   params — same shape as `useSvgBuildingInteraction` params
 * @param {object}   refs
 * @param {{ current: string|null }} refs.lastClickedRef
 * @param {{ current: Element|null }} refs.previewedPathRef
 * @returns {(containerEl: Element|null) => () => void}
 */
export function createBuildingWireListeners(
  { 
    areSoldOutBuildingsActive,
    soldUnitsAccessible,
    buildingsData, 
    activeBuilding, 
    onOutlineClick, 
    onPreviewChange, 
    deviceLayout, 
    currentFrameRef, 
    isDraggingRef, 
  },
  refs,
) {
  return (containerEl) => {
    if (!containerEl) return () => {}

    const { lastClickedRef, previewedPathRef } = refs
    const byIdCad   = new Map((buildingsData ?? []).map(b => [b.id_cad, b]))
    const isMobile  = deviceLayout?.isMobile ?? false

    // Returns true when the path is inside the currently active g[data-frame] group,
    // or when the SVG has no frame groups (VisualizationView / flat outline).
    const isInActiveFrame = (el) => {
      if (!currentFrameRef) return true
      const group = el.closest('g[data-frame]')
      if (!group) return true
      return parseInt(group.dataset.frame, 10) === currentFrameRef.current
    }

    const paths = Array.from(containerEl.querySelectorAll('path[id]')).filter(
      el => isBuildingPathId(el.id) && byIdCad.has(el.id)
    )

    const controller = new AbortController()
    const { signal } = controller

    for (const path of paths) {
      const idCad    = path.id
      const building = byIdCad.get(idCad)

      path.classList.toggle(SVG_OUTLINE_CLASS.ACTIVE, idCad === activeBuilding)

      const mode = resolveBuildingPreviewMode({
        soldUnitsAccessible,
        areSoldOutBuildingsActive,
        building,
      })
      const strategy = PREVIEW_MODE_STRATEGY[mode]
      const isInteractive = strategy?.isInteractive ?? false

      path.style.cursor = isInteractive ? 'pointer' : 'default'

      if (!isMobile) {
        path.addEventListener('mouseover', (e) => {
          if (isDraggingRef?.current) return
          if (!isInActiveFrame(path)) return
          path.classList.add(SVG_OUTLINE_CLASS.HOVERED)
          onPreviewChange?.({ 
            type: HERO_OUTLINE.BUILDINGS, 
            building,
            mode,
            cursorPos: { x: e.clientX, y: e.clientY } 
          })
        }, { signal })

        path.addEventListener('mouseleave', () => {
          if (!isInActiveFrame(path)) return
          path.classList.remove(SVG_OUTLINE_CLASS.HOVERED)
          onPreviewChange?.(null)
        }, { signal })

        path.addEventListener('click', () => {
          if (!isInActiveFrame(path)) return
          onOutlineClick?.(mode, { 
            type: HERO_OUTLINE.BUILDINGS, 
            building,
            mode,
          })
        }, { signal })

      } else {
        path.addEventListener('click', (e) => {
          if (!isInActiveFrame(path)) return
          if (isDraggingRef?.current) return

          if (lastClickedRef.current === idCad) {
            if (isInteractive) {
              onPreviewChange?.(null)
              onOutlineClick?.(mode, { 
                type: HERO_OUTLINE.BUILDINGS, 
                building,
                mode,
              })
            }
          } else {
            lastClickedRef.current = idCad
            onPreviewChange?.({
              type: HERO_OUTLINE.BUILDINGS,
              building,
              mode,
              cursorPos: { x: e.clientX, y: e.clientY },
              anchorRect: path.getBoundingClientRect(),
            })
          }
        }, { signal })
      }
    }

    const handlePreviewChangeSync = (e) => {
      const newIdCad = e.detail

      paths.forEach(path => {
        if (newIdCad && path.id === newIdCad) {
          path.classList.add(SVG_OUTLINE_CLASS.PREVIEW)
          lastClickedRef.current = newIdCad //double-tap update
        } else {
          path.classList.remove(SVG_OUTLINE_CLASS.PREVIEW)
        }
      })
    }

    containerEl.addEventListener('bim:preview-change', handlePreviewChangeSync)

    return () => {
      controller.abort()
      containerEl.removeEventListener('bim:preview-change', handlePreviewChangeSync)
      lastClickedRef.current = null
    }
  }
}

// ── Hook (thin React wrapper) ─────────────────────────────────────────────────

/**
 * useSvgBuildingInteraction
 *
 * Manages imperative event listeners on SVG building paths rendered by
 * `SvgOutlineLayer`. Returns a `wireListeners` function to be called from
 * `SvgOutlineLayer`'s internal `useEffect` after the SVG mounts.
 *
 * Path identification: `path` elements whose `id` matches 1–2 alphanumeric
 * characters are treated as building paths (id_cad format: `0A`, `0B`, `01`).
 *
 * Callbacks:
 * - `onBuildingClick(idCad)`  — caller: `setActiveBuilding(idCad)` + `setActiveView(BUILDING)`
 * - `onPreviewChange(data|null)` — `data = { type: HERO_OUTLINE.BUILDINGS, building, cursorPos }`
 *
 * @param {object}        params
 * @param {boolean}       params.areSoldOutBuildingsActive
 * @param {boolean}       params.soldUnitsAccessible
 * @param {object[]}      params.buildingsData
 * @param {string|null}   params.activeBuilding
 * @param {function}      params.onBuildingClick
 * @param {function}      params.onOutlineClick
 * @param {function}      [params.onPreviewChange]
 * @param {{ isMobile: boolean }} [params.deviceLayout]
 *
 * @returns {{ wireListeners: (containerEl: Element|null) => () => void }}
 */
export function useSvgBuildingInteraction(params) {
  const lastClickedRef   = useRef(null)
  const previewedPathRef = useRef(null)

  const wireListeners = useCallback(
    (containerEl) => createBuildingWireListeners(params, { lastClickedRef, previewedPathRef })(containerEl),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      params.areSoldOutBuildingsActive,
      params.soldUnitsAccessible,
      params.buildingsData, 
      params.activeBuilding, 
      params.onBuildingClick, 
      params.onOutlineClick,
      params.onPreviewChange, 
      params.deviceLayout, 
    ],
    // params.currentFrameRef and params.isDraggingRef are stable ref objects — intentionally excluded from deps
  )

  return { wireListeners }
}

/**
 * Resolves preview mode and interactivity of a tier outline.
 *
 * Hierarchy:
 * 1. Phasing of tiers building (currently_on_sale === false) -> inherited by the tier
 * 2. Active flag from CMS (is_active === false)
 * 3. Sold-out status (isSoldOut) with CMS setting
 * 4. Default mode (NORMAL_OUTLINE)
 *
 * @param {object} params
 * @param {object|null} params.building                  - Building object from buildingsData
 * @param {boolean} [params.areSoldOutBuildingActive]    - CMS Flag: are fully sold-out buildings visible/accessible in app (Heros ModelView)
 * @param {boolean} [params.soldUnitsAccessible]         - CMS Flag: are sold products viewable - overrides areSoldOutTiersActive
 * @returns {string} Value from OUTLINE_PREVIEW_MODE
 */
export function resolveBuildingPreviewMode({
  building,
  areSoldOutBuildingsActive = false,
  soldUnitsAccessible = false,
}) {
  if (!building) return OUTLINE_PREVIEW_MODE.DISABLED_INACTIVE

  if (building.currently_on_sale === false) {
    if (building.allow_inquiry)  return OUTLINE_PREVIEW_MODE.OTHER_STAGE_INQUIRY
    if (building.redirect_to_app) return OUTLINE_PREVIEW_MODE.OTHER_STAGE_REDIRECT
    return OUTLINE_PREVIEW_MODE.OTHER_STAGE
  }

  if (building.isActive === false || building.hasActiveTiers === false) {
    return OUTLINE_PREVIEW_MODE.DISABLED_INACTIVE
  }

  if (building.isSoldOut && !areSoldOutBuildingsActive && !soldUnitsAccessible) {
    return OUTLINE_PREVIEW_MODE.DISABLED_SOLD_OUT
  }

  return OUTLINE_PREVIEW_MODE.NORMAL_OUTLINE
}